import { Status } from '@constants/status';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { drawerWidth } from '../../constants/wizard-drawer';
import { entityVariant } from './constants';
import type { ChipStatus, SubwayEntityVariant } from './types';

export const StyledMenuItem = styled(MenuItem)<{
  type?: string;
}>(({ theme, type }) => ({
  '&.MuiMenuItem-root': {
    color: type === 'warning' ? theme.palette.red[1] : 'inherit',
  },
}));

export const StyledStatusChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'status',
})<{ status: ChipStatus }>(({ theme, status }) => ({
  height: theme.spacing(2.25),
  '& .MuiChip-label': {
    fontSize: '0.5rem',
    fontWeight: 400,
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  },
  ...(status === Status.DRAFT && {
    backgroundColor: theme.palette.blue[10],
    color: theme.palette.blue[6],
  }),
  ...(status === Status.ACTIVE && {
    backgroundColor: theme.palette.green[10],
    color: theme.palette.green[6],
  }),
  ...(status === Status.INACTIVE && {
    backgroundColor: theme.palette.red[5],
    color: theme.palette.red[1],
  }),
  ...(status === Status.PENDING && {
    backgroundColor: theme.palette.grey[11],
    color: theme.palette.grey[6],
  }),
  ...(status === Status.ARCHIVED && {
    backgroundColor: theme.palette.grey[11],
    color: theme.palette.grey[2],
  }),
  ...(status === 'UNSAVED' && {
    backgroundColor: theme.palette.common.offWhite,
    color: theme.palette.grey[2],
  }),
}));

export const StyledMenuDotButton = styled(IconButton)(({ theme }) => ({
  height: theme.spacing(2.625),
  width: theme.spacing(3.125),
  padding: `${theme.spacing(0.5)} ${theme.spacing(2)} \
  ${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
  '& svg': {
    height: '100%',
  },
}));

export const StyledSubwayNavContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'variant',
})<{ selected: boolean; variant: SubwayEntityVariant }>(
  ({ theme, selected, variant }) => ({
    position: 'relative',
    backgroundColor: selected ? theme.palette.blue[11] : 'transparent',
    padding:
      variant === entityVariant.launch
        ? theme.spacing(1.5, 0)
        : `${theme.spacing(1)} 0`,
    '&:hover': {
      backgroundColor: theme.palette.blue[11],
      cursor: 'pointer',
    },
    '&:after': selected
      ? {
          content: "''",
          position: 'absolute',
          top: 0,
          left: `calc(100% - ${theme.spacing(0.5)})`,
          height: '100%',
          width: theme.spacing(0.5),
          background: theme.palette.blue[6],
          borderRadius: theme.spacing(0.25, 0, 0, 0.25),
        }
      : {},
  })
);

export const StyledSubwayEntityRow = styled(Stack, {
  shouldForwardProp: (prop: string) => !['variant', 'isTitle'].includes(prop),
})<{ variant: SubwayEntityVariant }>(({ theme, variant }) => ({
  paddingLeft: variant === 'group' ? theme.spacing(6) : theme.spacing(4.5),
}));

export const StyledIdTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  fontSize: '0.625rem',
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  color: selected ? theme.palette.text.primary : theme.palette.grey[4],
}));

export const StyledTitleTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: '1rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: selected ? theme.palette.text.primary : theme.palette.grey[2],
  ...(selected && {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  }),
}));

export const StyledCreativeContainer = styled(Stack, {
  shouldForwardProp: prop => prop !== 'variant' && prop !== 'selected',
})<{ variant: SubwayEntityVariant; selected?: boolean }>(
  ({ theme, variant, selected }) => ({
    paddingLeft:
      variant === entityVariant.group ? theme.spacing(6) : theme.spacing(4.5),
    paddingBottom: selected ? theme.spacing(1.5) : 'auto',
    '& > *': {
      paddingLeft: theme.spacing(1),
      paddingTop: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  })
);

export const StyledCreativeTypography = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: theme.palette.text.primary,
}));

export const StyledSubwayHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  position: 'sticky',
  top: 0,
  minHeight: theme.spacing(7.5),
  marginBottom: theme.spacing(2),
  paddingRight: theme.spacing(1.875),
  paddingLeft: theme.spacing(3.75),
  alignItems: 'center',
  zIndex: 2,
  background: theme.palette.grey[11],
}));

export const StyledSubwayHeaderActions = styled(Stack)({
  flex: 1,
  justifyItems: 'center',
  justifyContent: 'center',
});

export const StyledSubwayWrapper = styled(Stack)({
  height: '100%',
  position: 'relative',
  flexDirection: 'row',
});

export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    borderRight: `${theme.spacing(0.125)} solid ${theme.palette.grey[9]}`,
    paddingTop: theme.spacing(8),
    left: theme.spacing(14),
    boxShadow: '2px 0px 8px 0px #00000026',
    height: '100%',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    paddingBottom: theme.spacing(2),
    zIndex: 999,
    width: drawerWidth,
    boxSizing: 'border-box',
    background: theme.palette.grey[11],
  },
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(4),
  height: theme.spacing(4),
}));

export const StyledDrawerOpenWrapper = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(9),
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 1,
}));

export const StyledDrawerOpenButton = styled(StyledIconButton, {
  shouldForwardProp: prop => prop !== 'open',
})<{
  open?: boolean;
}>(({ open, theme }) => ({
  boxSizing: 'border-box',
  transform: 'rotate(180deg)',
  position: 'sticky',
  top: theme.spacing(1.7),
  left: theme.spacing(3),
  zIndex: 2,
  display: open ? 'none' : 'inline-flex',
  visibility: open ? 'hidden' : 'visible',
  transition: theme.transitions.create('visibility', {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.standard,
  }),
}));

export const StyledDrawerCloseButton = styled(StyledIconButton, {
  shouldForwardProp: prop => prop !== 'open',
})<{
  open?: boolean;
}>(({ open }) => ({
  display: !open ? 'inline-flex' : 'none',
}));

export const StyledCollapseIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  width: theme.spacing(2),
  color: theme.palette.grey[4],
}));

export const StyledSortButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(3.25),
  color: theme.palette.grey[4],
  height: theme.spacing(3.25),
  justifyContent: 'center',
  alignItems: 'center',
  ':hover': {
    color: theme.palette.common.black,
  },
  svg: {
    paddingTop: theme.spacing(0.25),
    width: theme.spacing(1.9375),
    height: theme.spacing(1.75),
  },
}));

export const StyledCheckIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.palette.blue[6],
}));
export const StyledSubwayAddAdGroupButton = styled(Button)(
  ({ theme, disabled }) => ({
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: '1rem',
    height: theme.spacing(2.25),
    padding: theme.spacing(0, 1.5, 0, 3),
    color: theme.palette.blue[6],
    justifyContent: 'flex-start',
    '&.Mui-disabled': {
      color: theme.palette.grey[4],
    },
    '& .MuiButton-startIcon': {
      margin: theme.spacing(0, 0.5, 0, 0),
      color: disabled ? theme.palette.grey[4] : theme.palette.blue[6],
    },
  })
);
